"use client";

import { Plus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Pricingandforecastingfaq() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const faqs = [{
    index: "01",
    question: "How does dynamic pricing impact my hotel's revenue?",
    answer: "Dynamic pricing adjusts your room rates in real-time based on market demand, competitor activity, and historical booking patterns. This ensures you capture maximum revenue during high-demand periods while maintaining occupancy during slower seasons."
  }, {
    index: "02",
    question: "What data sources do you use for forecasting?",
    answer: "We integrate data from your Property Management System (PMS), Channel Manager, and OTA extranets. We also analyze local event calendars, flight search trends, and competitor rate parity to build highly accurate demand forecasts."
  }, {
    index: "03",
    question: "Are your revenue management services performance-based?",
    answer: "We offer flexible engagement models, including fixed-fee consulting and performance-based structures. Our goal is to align our incentives with your revenue growth, ensuring a transparent and mutually beneficial partnership."
  }, {
    index: "04",
    question: "Can you help with OTA listing optimization?",
    answer: "Yes, we specialize in OTA listing management. We optimize your content, images, and rate strategies on platforms like Booking.com and Expedia to improve your visibility, conversion rates, and overall ranking."
  }, {
    index: "05",
    question: "Do you provide support for hotel tech stack selection?",
    answer: "Absolutely. We evaluate your current technology and recommend the best-in-class PMS, RMS, and booking engines that integrate seamlessly to streamline your operations and improve data accuracy."
  }, {
    index: "06",
    question: "How do you handle rate parity across channels?",
    answer: "We monitor your rates across all distribution channels 24/7. Our automated alerts and manual intervention strategies ensure your direct booking rates remain competitive, preventing undercutting by OTAs."
  }, {
    index: "07",
    question: "Is there a minimum contract period for your services?",
    answer: "We believe in the value we provide. While we recommend a minimum 3-month engagement to see significant results from revenue strategies, we offer flexible terms to suit the needs of independent hotels and boutique properties."
  }, {
    index: "08",
    question: "How do I get started with a revenue audit?",
    answer: "You can request a free initial revenue audit through our contact page. We will analyze your current performance metrics and provide a high-level overview of potential growth opportunities for your property."
  }];
  return <section data-section-id="2780" id="pricingandforecastingfaq" className="bg-slate-50 text-slate-900 py-24 md:py-32 px-6 md:px-12 lg:px-20">
      <div className="max-w-7xl mx-auto">
        <div ref={ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {}
          <div className="lg:col-span-12 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-12 mb-12 lg:mb-20">
            <div className="lg:col-span-3">
              <span className="font-default text-xs uppercase tracking-widest text-blue-700">
                § revenue insights
              </span>
            </div>
            <div className="lg:col-span-9">
              <h2 className="font-default text-4xl md:text-5xl lg:text-[56px] leading-[1.05] text-slate-900">
                Pricing & <em className="italic font-light">Forecasting</em>
              </h2>
              <p className="font-default italic text-lg md:text-xl text-slate-700 mt-6 max-w-2xl leading-relaxed">
                Common questions regarding our revenue management and forecasting strategies.
              </p>
            </div>
          </div>

          {}
          <div className="lg:col-span-9 lg:col-start-2">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, i) => <AccordionItem key={faq.index} value={faq.index} data-index={i} className="border-b border-slate-200 border-t-0 first:border-t first:border-t-slate-200">
                  <AccordionTrigger className="group py-8 hover:no-underline [&>svg]:hidden">
                    <div className="flex items-baseline gap-6 md:gap-10 flex-1 text-left">
                      <span className="font-default text-xs text-blue-700 tracking-wider shrink-0 pt-1">
                        {faq.index}
                      </span>
                      <span className="font-default text-lg md:text-[22px] leading-snug text-slate-900 font-semibold">
                        {faq.question}
                      </span>
                    </div>
                    <span className="ml-6 shrink-0 text-blue-700 transition-transform duration-300 ease-out group-data-[state=open]:rotate-45">
                      <Plus className="size-5" strokeWidth={1.25} />
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-8">
                    <div className="flex items-start gap-6 md:gap-10">
                      <span className="font-default text-xs text-transparent shrink-0 select-none">
                        {faq.index}
                      </span>
                      <p className="font-default text-[17px] leading-[1.7] text-slate-700 max-w-2xl">
                        {faq.answer}
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>

          {}
          <div className="lg:col-span-2 lg:col-start-11 mt-12 lg:mt-0">
            <div className="lg:sticky lg:top-32 border-t border-slate-200 pt-8 lg:border-t-0 lg:pt-0">
              <span className="font-default text-xs uppercase tracking-widest text-blue-700 block mb-6">
                § need clarity?
              </span>
              <p className="font-default italic text-lg leading-snug text-slate-900 mb-6">
                Contact us for a custom strategy.
              </p>
              <Link to="/ContactPage" className="font-default text-xs uppercase tracking-widest text-blue-700 border-b border-blue-700/40 pb-1 inline-block transition-colors hover:text-blue-900 hover:border-blue-900">
                Get in touch &rarr;
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>;
}